import { Table, TableContainer, TableHead, TableBody, TableRow, TableCell, Skeleton } from '@mui/material';
import { CerebroDrawer, CerebroHeading, CerebroIconButton, CerebroSecondaryButton } from '../../../shared/components';
import { useTermSheetFacilityFeesQuery, useTermSheetFeesQuery } from '../../../shared/hooks';
import { TermSheetFacility, TermSheetFacilityFee, TermSheetFee } from '../../../shared/models';
import { EnumUtils, NumberUtils } from '../../../shared/utils';
import React from 'react';
import { FeeFormDialog } from './FeeFormDialog';
import { DeleteFeeDialog } from './DeleteFeeDialog';
import { EmptyGuid } from '../../../shared/constants';
import { FeeActionButton } from './FeeActionButton';
import { NotesFormDialog } from './NotesFormDialog';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faNote } from '@fortawesome/pro-duotone-svg-icons';

interface FacilityFeeDrawerProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	facility: TermSheetFacility | null;
	close: () => void;
}

export const FacilityFeeDrawer: React.FC<FacilityFeeDrawerProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	facility,
	close,
}) => {
	const [feeForm, setFeeForm] = React.useState<TermSheetFacilityFee | null>(null);
	const [feeDelete, setFeeDelete] = React.useState<TermSheetFacilityFee | null>(null);
	const [openNotes, setOpenNotes] = React.useState<TermSheetFacilityFee | null>(null);

	const termSheetFeesQuery = useTermSheetFeesQuery();

	const feesQuery = useTermSheetFacilityFeesQuery(dataRoomGuid, termSheetGuid, revisionGuid, facility?.guid, {
		enabled: facility != null,
		refetchOnWindowFocus: false,
	});
	const isFetching = feesQuery.isLoading || feesQuery.isFetching;
	const isFeesFetching = termSheetFeesQuery.isLoading || termSheetFeesQuery.isFetching;

	const onCloseDialogs = () => {
		setFeeForm(null);
		setFeeDelete(null);
		setOpenNotes(null);
	};

	const createNewFee = () => {
		setFeeForm({
			id: 0,
			guid: EmptyGuid,
			termSheetFeeGuid: EmptyGuid,
			termSheetFee: {} as TermSheetFee,
			termSheetFeeType: 'Currency',
			feeFrequency: 'Annually',
			feeAmount: 0,
			feeNotes: ''
		});
	};

	if (isFetching || isFeesFetching) {
		return (
			<CerebroDrawer open={facility != null} onClose={close}>
				<CerebroHeading>Facility Fees</CerebroHeading>
				<Skeleton height={60} width={600} />
				<Skeleton height={60} width={600} />
				<Skeleton height={60} width={600} />
			</CerebroDrawer>
		);
	}

	return (
		<CerebroDrawer open={facility != null} onClose={close}>
			<CerebroHeading>Facility Fees</CerebroHeading>

			<CerebroSecondaryButton onClick={() => createNewFee()} sx={{ marginBottom: 2, marginTop: 3 }}>
				Add Fee
			</CerebroSecondaryButton>

			<TableContainer>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>Fee</TableCell>
							<TableCell>Value</TableCell>
							<TableCell>Type</TableCell>
							<TableCell>Frequency</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isFetching && (
							<TableRow>
								<TableCell colSpan={4}>
									<Skeleton height={60} />
								</TableCell>
							</TableRow>
						)}

						{feesQuery.isSuccess && (
							<>
								{feesQuery.data.map(item => {
									return (
										<TableRow key={item.guid}>
											<TableCell>
												{item.termSheetFee.feeName}

												{item.feeNotes != '' && item.feeNotes != undefined && (
													<CerebroIconButton
														disabled={true}
														sx={theme => ({ marginTop: 0, color: theme.palette.info.main, paddingLeft: 0 })}
													>
														<FontAwesomeIcon icon={faNote} size="xs" />
													</CerebroIconButton>
												)}

											</TableCell>
											<TableCell>
												{isCurrencyFee(item) ? (
													NumberUtils.FormatCurrency(item.feeAmount)
												) : (
													<>{item.feeAmount}%</>
												)}
											</TableCell>
											<TableCell>
												{EnumUtils.GetTermSheetFeeTypesDescription(item.termSheetFeeType)}
											</TableCell>
											<TableCell>
												{EnumUtils.GetTermSheetFeeFrequenciesDescription(item.feeFrequency)}
											</TableCell>
											<TableCell>
												<FeeActionButton
													fee={item}
													openNotes={setOpenNotes}
													onEdit={setFeeForm}
													onDelete={setFeeDelete}
												/>
											</TableCell>
										</TableRow>
									);
								})}
								{feesQuery.data.length == 0 && (
									<TableRow>
										<TableCell colSpan={5}>No fee currently associated to this facility.</TableCell>
									</TableRow>
								)}
							</>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<FeeFormDialog
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={facility}
				fee={feeForm}
				termSheetFees={termSheetFeesQuery.data}
				close={onCloseDialogs}
			/>

			<DeleteFeeDialog
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={facility}
				fee={feeDelete}
				close={onCloseDialogs}
			/>

			<NotesFormDialog
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={facility}
				fee={openNotes}
				close={onCloseDialogs}
			/>

		</CerebroDrawer>
	);
};

function isCurrencyFee(value: TermSheetFacilityFee): boolean {
	return value.termSheetFeeType == 'Currency';
}
